import React from 'react';
import { XCircle, AlertTriangle, PauseCircle, RotateCcw, Zap, Hourglass, CheckCircle2, ShieldCheck, MinusCircle } from 'lucide-react';
import { UnidadeConsumidora } from '../../types';

export type StatusRateioUc =
    | 'cancelada'
    | 'excesso_saldo'
    | 'pausa_retirada_impedimento'
    | 'retorno_ao_rateio'
    | 'rateio_ativo'
    | 'aguardando_inclusao'
    | 'apta_distribuidora'
    | 'aprovada_cadastro'
    | 'inativa';

type UcParaStatusRateio = Pick<UnidadeConsumidora,
    'situacao' | 'cadastro_aprovado' | 'rateio_motivo_travamento' | 'dados_tecnicos_equatorial' |
    'rateio_unidade_geradora_id' | 'unidade_geradora'>;

const SITUACOES_RATEIO_ATIVO = ['Ativa', '1° Ciclo', '2° Ciclo', 'Ativa-S/-Rateio'];

/**
 * Status de rateio da UC, derivado de campos já existentes (situacao, cadastro_aprovado,
 * rateio_motivo_travamento, dados_tecnicos_equatorial.checks.atualizada, rateio_unidade_geradora_id,
 * unidade_geradora) — nunca gravado, sempre recalculado. Prioridade de cima pra baixo: a primeira
 * condição que bater vence.
 */
export function getStatusRateioUc(uc: UcParaStatusRateio): StatusRateioUc {
    if (uc.situacao === 'Cancelada') return 'cancelada';
    if (uc.situacao === 'Pausada' && uc.rateio_motivo_travamento === 'excesso_saldo') return 'excesso_saldo';
    if (uc.situacao === 'Pausada') return 'pausa_retirada_impedimento';
    if (uc.situacao === 'Reativacao') return 'retorno_ao_rateio';
    if (uc.situacao && SITUACOES_RATEIO_ATIVO.includes(uc.situacao)) return 'rateio_ativo';
    const cadastroOk = uc.cadastro_aprovado === true;
    const aptaDistribuidora = uc.dados_tecnicos_equatorial?.checks?.atualizada === true;
    const semVinculo = !uc.rateio_unidade_geradora_id && !uc.unidade_geradora;
    if (cadastroOk && aptaDistribuidora && semVinculo) return 'aguardando_inclusao';
    if (aptaDistribuidora) return 'apta_distribuidora';
    if (cadastroOk) return 'aprovada_cadastro';
    return 'inativa';
}

export const STATUS_RATEIO_UC_META: Record<StatusRateioUc, { label: string; className: string; icon: React.ReactNode }> = {
    cancelada: {
        label: 'UC cancelada',
        className: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200/80 dark:border-red-800/40',
        icon: React.createElement(XCircle, { size: 12 }),
    },
    excesso_saldo: {
        label: 'Excesso de saldo',
        className: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200/80 dark:border-blue-800/40',
        icon: React.createElement(AlertTriangle, { size: 12 }),
    },
    pausa_retirada_impedimento: {
        label: 'Pausa, retirada ou impedimento',
        className: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200/80 dark:border-amber-800/40',
        icon: React.createElement(PauseCircle, { size: 12 }),
    },
    retorno_ao_rateio: {
        label: 'Retorno ao rateio',
        className: 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400 border-violet-200/80 dark:border-violet-800/40',
        icon: React.createElement(RotateCcw, { size: 12 }),
    },
    rateio_ativo: {
        label: 'Rateio ativo',
        className: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200/80 dark:border-green-800/40',
        icon: React.createElement(Zap, { size: 12 }),
    },
    aguardando_inclusao: {
        label: 'Aguardando inclusão',
        className: 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-400 border-cyan-200/80 dark:border-cyan-800/40',
        icon: React.createElement(Hourglass, { size: 12 }),
    },
    apta_distribuidora: {
        label: 'UC apta na distribuidora',
        className: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-200/80 dark:border-emerald-800/40',
        icon: React.createElement(CheckCircle2, { size: 12 }),
    },
    aprovada_cadastro: {
        label: 'UC aprovada no Cadastro',
        className: 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400 border-teal-200/80 dark:border-teal-800/40',
        icon: React.createElement(ShieldCheck, { size: 12 }),
    },
    inativa: {
        label: 'UC inativa',
        className: 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200/80 dark:border-slate-700/80',
        icon: React.createElement(MinusCircle, { size: 12 }),
    },
};
